import { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Save, Trash2 } from 'lucide-react';
import { useThematicNodes } from '@/hooks/useThematicNodes';
import type { ThematicNodeWithEntity, DocumentRole, NodeStatus } from '@/types/thematicMaps';
import { DOCUMENT_ROLES, NODE_STATUSES } from '@/types/thematicMaps';

interface NodeSheetProps {
  node: ThematicNodeWithEntity | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mapId: string;
}

const ROLE_LABELS: Record<DocumentRole, string> = {
  definition: 'Definicja',
  how_to: 'How-to',
  comparison: 'Porównanie',
  troubleshooting: 'Troubleshooting',
  glossary: 'Słownik',
  history: 'Historia',
  commercial: 'Komercyjny',
  protocol: 'Protokół',
  guide: 'Przewodnik',
};

const STATUS_LABELS: Record<NodeStatus, string> = {
  planned: 'Planned',
  draft: 'Draft',
  published: 'Published',
  refresh: 'Refresh',
  deprecated: 'Deprecated',
};

export function NodeSheet({ node, open, onOpenChange, mapId }: NodeSheetProps) {
  const { updateNode, deleteNode } = useThematicNodes(mapId);
  const [saving, setSaving] = useState(false);

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [documentRole, setDocumentRole] = useState<DocumentRole>('definition');
  const [nodeStatus, setNodeStatus] = useState<NodeStatus>('planned');
  const [primaryIntent, setPrimaryIntent] = useState('');
  const [canonicalQuestion, setCanonicalQuestion] = useState('');
  const [primaryEntityRaw, setPrimaryEntityRaw] = useState('');

  useEffect(() => {
    if (node) {
      setTitle(node.title);
      setSlug(node.slug);
      setDocumentRole(node.document_role);
      setNodeStatus(node.node_status);
      setPrimaryIntent(node.primary_intent);
      setCanonicalQuestion(node.canonical_question);
      setPrimaryEntityRaw(node.primary_entity_raw);
    }
  }, [node?.id]);

  if (!node) return null;

  const handleSave = async () => {
    setSaving(true);
    await updateNode(node.id, {
      title,
      slug,
      document_role: documentRole,
      node_status: nodeStatus,
      primary_intent: primaryIntent,
      canonical_question: canonicalQuestion,
      primary_entity_raw: primaryEntityRaw,
    });
    setSaving(false);
  };

  const handleDelete = async () => {
    if (confirm(`Usunąć node "${node.title}"?`)) {
      await deleteNode(node.id);
      onOpenChange(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[480px] sm:max-w-[480px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-left pr-6">{node.title}</SheetTitle>
        </SheetHeader>

        <div className="space-y-4 mt-6">
          <div className="space-y-2">
            <Label>Tytuł</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>

          <div className="space-y-2">
            <Label>Slug</Label>
            <Input value={slug} onChange={(e) => setSlug(e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Rola dokumentu</Label>
              <Select value={documentRole} onValueChange={(v) => setDocumentRole(v as DocumentRole)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_ROLES.map((role) => (
                    <SelectItem key={role} value={role}>
                      {ROLE_LABELS[role]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={nodeStatus} onValueChange={(v) => setNodeStatus(v as NodeStatus)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NODE_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>
                      {STATUS_LABELS[status]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Primary Intent</Label>
            <Input value={primaryIntent} onChange={(e) => setPrimaryIntent(e.target.value)} />
          </div>

          <div className="space-y-2">
            <Label>Canonical Question</Label>
            <Textarea
              value={canonicalQuestion}
              onChange={(e) => setCanonicalQuestion(e.target.value)}
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label>Primary Entity (RAW)</Label>
            <Input value={primaryEntityRaw} onChange={(e) => setPrimaryEntityRaw(e.target.value)} />
          </div>

          {node.primary_entity && (
            <div className="space-y-1">
              <Label className="text-muted-foreground text-xs">Linked Entity</Label>
              <Badge variant="outline">
                {node.primary_entity.canonical_name}
              </Badge>
            </div>
          )}

          {node.required_entities_raw && node.required_entities_raw.length > 0 && (
            <div className="space-y-1">
              <Label className="text-muted-foreground text-xs">Required Entities (RAW)</Label>
              <div className="flex flex-wrap gap-1">
                {node.required_entities_raw.map((e, i) => (
                  <Badge key={i} variant="secondary">{e}</Badge>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-between pt-4 border-t">
            <Button variant="destructive" size="sm" onClick={handleDelete}>
              <Trash2 className="mr-2 h-3.5 w-3.5" />
              Usuń
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              <Save className="mr-2 h-4 w-4" />
              {saving ? 'Zapisywanie...' : 'Zapisz'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
